export default {
  name: "BolideAssistantMessages",

  props: {
    messages: {
      type: Array,
      default: () => [],
    },
    has_older_messages: Boolean,
    is_loading_older_messages: Boolean,
  },

  emits: ["load-older-messages"],

  watch: {
    messages: {
      deep: true,
      handler() {
        if (this.is_loading_older_messages) return;
        this.scroll_to_bottom({ settle: true });
      },
    },
  },

  mounted() {
    this.scroll_to_bottom({ settle: true });
  },

  methods: {
    handle_scroll(event) {
      if (!this.has_older_messages || this.is_loading_older_messages) return;
      if (event.currentTarget.scrollTop < 80) this.$emit("load-older-messages");
    },

    capture_scroll_state() {
      const element = this.$refs.messages_body;
      if (!element) return null;
      return ({
        scroll_height: element.scrollHeight,
        scroll_top: element.scrollTop,
      });
    },

    restore_scroll_state(scroll_state) {
      if (!scroll_state) return;
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const element = this.$refs.messages_body;
          if (!element) return;
          element.scrollTop = element.scrollHeight
            - scroll_state.scroll_height
            + scroll_state.scroll_top;
        });
      });
    },

    get_thinking_status(message) {
      const steps = Array.isArray(message?.thinking_steps) ? message.thinking_steps : [];

      if (steps.some((step) => step.status === "failed")) {
        return "failed";
      }

      if (steps.length && steps.every((step) => step.status === "complete")) {
        return "complete";
      }

      return "active";
    },

    get_thinking_status_label(message) {
      const status = this.get_thinking_status(message);

      if (status === "failed") {
        return "Needs attention";
      }

      if (status === "complete") {
        return "Ready";
      }

      const steps = Array.isArray(message?.thinking_steps) ? message.thinking_steps : [];
      const completed_count = steps.filter((step) => step.status === "complete").length;

      return steps.length ? `${completed_count}/${steps.length}` : "Starting";
    },

    get_message_created_at(message) {
      if (message?.created_at) {
        return message.created_at;
      }

      const timestamp_from_id = String(message?.id || "").split("-")[0];
      const timestamp = Number(timestamp_from_id);

      return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : "";
    },

    format_message_timestamp(message) {
      if (message?.is_typing) {
        return "";
      }

      const created_at = this.get_message_created_at(message);
      if (!created_at) {
        return "";
      }

      const date = new Date(created_at);
      if (Number.isNaN(date.getTime())) {
        return "";
      }

      return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
    },

    get_readable_message_text(message) {
      const text = String(message?.text || "").trim();
      if (!text || message?.sender !== "assistant") {
        return text;
      }

      if (text.length < 180 || /\n\s*\n/.test(text)) {
        return text;
      }

      let formatted_text = text
        .replace(
          /\s+(?=(Investigate|Review|Check|Validate|Compare|Confirm|Prioritize|Do not|Treat|Use|Flag|Escalate)\b)/g,
          "\n\n"
        )
        .replace(/:\s+(?=\d+\s)/g, ":\n")
        .replace(/\s+(?=\d+\s+(suspected|empty|unusually|daily|hourly)\b)/gi, "\n");

      if (formatted_text === text) {
        formatted_text = text.replace(/([.!?])\s+(?=[A-Z][a-z])/g, "$1\n\n");
      }

      return formatted_text.trim();
    },

    scroll_messages_body_to_bottom() {
      const el = this.$refs.messages_body;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },

    scroll_to_bottom({ settle = false } = {}) {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.scroll_messages_body_to_bottom();
          requestAnimationFrame(() => {
            this.scroll_messages_body_to_bottom();
          });
        });

        if (settle) {
          setTimeout(() => {
            this.scroll_messages_body_to_bottom();
          }, 80);
          setTimeout(() => {
            this.scroll_messages_body_to_bottom();
          }, 220);
        }
      });
    },
  },
};
